import type { Camera } from "./geometry";

/** Browser view preference, deliberately absent from the document schema. */
export interface WorkspaceGridSettings {
  enabled: boolean;
  color: string;
  opacity: number;
}
export function workspaceGridSettings(value: unknown): WorkspaceGridSettings {
  const v = (
    value && typeof value === "object" ? value : {}
  ) as Partial<WorkspaceGridSettings>;
  return {
    enabled: v.enabled === true,
    color:
      typeof v.color === "string" && /^#[\da-f]{6}$/i.test(v.color)
        ? v.color.toLowerCase()
        : "#94a3b8",
    opacity:
      typeof v.opacity === "number" && Number.isFinite(v.opacity)
        ? Math.max(0, Math.min(1, v.opacity))
        : 1,
  };
}
export interface GridRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Bounded screen-space hairlines, anchored to world zero and cut around the page,
 * including a hidden page background. No scene nodes, hit areas or snap targets. */
export function workspaceGridLayout(
  camera: Camera,
  page: { width: number; height: number },
  width: number,
  height: number,
  dpr: number,
  settings: WorkspaceGridSettings | undefined,
  background: string,
): { color: string; rectangles: GridRectangle[] } {
  const rectangles: GridRectangle[] = [];
  if (
    !settings?.enabled ||
    settings.opacity <= 0 ||
    camera.zoom <= 0 ||
    !Number.isFinite(camera.zoom)
  )
    return { color: background, rectangles };
  // Coarsen when zooming out: at most one line per 24 CSS px on either axis.
  const step =
    40 * 2 ** Math.max(0, Math.ceil(Math.log2(24 / (40 * camera.zoom))));
  const spacing = step * camera.zoom;
  const left = camera.x,
    top = camera.y;
  const right = left + page.width * camera.zoom,
    bottom = top + page.height * camera.zoom;
  const add = (x: number, y: number, w: number, h: number) => {
    if (w > 0 && h > 0) rectangles.push({ x, y, width: w, height: h });
  };
  for (let i = Math.ceil(-left / spacing); left + i * spacing < width; i++) {
    const x = Math.round((left + i * spacing) * dpr) / dpr;
    // Split rectangles, not just their centerline, to leave the page completely clean.
    if (x + 1 <= left || x >= right) add(x, 0, 1, height);
    else {
      add(x, 0, 1, Math.max(0, Math.min(height, top)));
      add(x, Math.max(0, bottom), 1, height - Math.max(0, bottom));
      add(
        x,
        Math.max(0, top),
        Math.max(0, left - x),
        Math.min(height, bottom) - Math.max(0, top),
      );
      add(
        Math.max(x, right),
        Math.max(0, top),
        x + 1 - Math.max(x, right),
        Math.min(height, bottom) - Math.max(0, top),
      );
    }
  }
  for (let i = Math.ceil(-top / spacing); top + i * spacing < height; i++) {
    const y = Math.round((top + i * spacing) * dpr) / dpr;
    if (y + 1 <= top || y >= bottom) add(0, y, width, 1);
    else {
      add(0, y, Math.max(0, Math.min(width, left)), 1);
      add(Math.max(0, right), y, width - Math.max(0, right), 1);
      add(
        Math.max(0, left),
        y,
        Math.min(width, right) - Math.max(0, left),
        Math.max(0, top - y),
      );
      add(
        Math.max(0, left),
        Math.max(y, bottom),
        Math.min(width, right) - Math.max(0, left),
        y + 1 - Math.max(y, bottom),
      );
    }
  }
  // Preblend with the workspace so intersections have the same subtle weight.
  const foreground = parseInt(settings.color.slice(1), 16),
    base = parseInt(background.slice(1), 16);
  const alpha = 0.22 * settings.opacity;
  const color =
    "#" +
    [16, 8, 0]
      .map((shift) =>
        Math.round(
          ((base >> shift) & 255) * (1 - alpha) +
            ((foreground >> shift) & 255) * alpha,
        )
          .toString(16)
          .padStart(2, "0"),
      )
      .join("");
  return { color, rectangles };
}
